import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { MoreHorizontal, Pencil, Trash2, Save, X } from 'lucide-react';
import { Badge } from '../../../../../components/ui/badge';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import apiService from '../../../../../services/api.service';
import DeleteApiKeyDialog from './DeleteApiKeyDialog';

function ApiKeyTableRow({ apiKey, onSuccess, onError, onApiKeyUpdated, onApiKeyDeleted }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (isMenuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 120,
      });
    }
  }, [isMenuOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const [editName, setEditName] = useState(apiKey.name);
  const [editExpirationDate, setEditExpirationDate] = useState(
    format(new Date(apiKey.expirationDate), "yyyy-MM-dd'T'HH:mm"),
  );
  const [nameError, setNameError] = useState('');

  // Convert scopes object to array of strings
  const scopesList = Object.keys(apiKey.scopes).reduce((acc, entity) => {
    const entityScopes = apiKey.scopes[entity].map((scope) => `${entity}:${scope}`);
    return acc.concat(entityScopes);
  }, []);

  const validateName = async (name) => {
    if (!name) {
      setNameError('Name is required');
      return false;
    }
    if (!name.match(/^[a-zA-Z0-9-:_]*$/)) {
      setNameError('Invalid characters');
      return false;
    }
    try {
      const existingNames = await apiService.admin.getApiKeysNames();
      if (existingNames.includes(name) && name !== apiKey.name) {
        setNameError('Name already exists');
        return false;
      }
    } catch (error) {
      console.error('Error validating name:', error);
    }
    setNameError('');
    return true;
  };

  const handleSave = async () => {
    const isValid = await validateName(editName);
    if (!isValid) return;

    setIsSaving(true);
    try {
      await apiService.admin.updateApiKey(apiKey._id, {
        name: editName,
        expirationDate: new Date(editExpirationDate).toISOString(),
      });
      onSuccess('API key updated successfully');
      setIsEditing(false);
      onApiKeyUpdated();
    } catch (error) {
      console.error('Error updating API key:', error);
      onError('Failed to update API key');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditName(apiKey.name);
    setEditExpirationDate(format(new Date(apiKey.expirationDate), "yyyy-MM-dd'T'HH:mm"));
    setNameError('');
    setIsEditing(false);
  };

  const handleDelete = async () => {
    try {
      await apiService.admin.deleteApiKey(apiKey._id);
      onSuccess('API key deleted successfully');
      setIsDeleteDialogOpen(false);
      onApiKeyDeleted();
    } catch (error) {
      console.error('Error deleting API key:', error);
      onError('Failed to delete API key');
    }
  };

  const formatDate = (dateString) => {
    if (dateString === 'not used') return 'Not used';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  const formatDateTime = (dateString) => {
    return format(new Date(dateString), 'MMM d, yyyy HH:mm');
  };

  return (
    <>
      <tr className="border-b border-border hover:bg-muted/50 transition-colors">
        {/* Key Name */}
        <td className="px-4 py-3">
          {isEditing ? (
            <div className="space-y-1">
              <Input
                value={editName}
                onChange={(e) => {
                  setEditName(e.target.value);
                  if (nameError) validateName(e.target.value);
                }}
                onBlur={() => validateName(editName)}
                className={nameError ? 'border-red-500' : ''}
              />
              {nameError && <p className="text-xs text-red-500">{nameError}</p>}
            </div>
          ) : (
            <span className="text-sm font-medium">{apiKey.name}</span>
          )}
        </td>

        {/* Prefix */}
        <td className="px-4 py-3">
          <code className="text-xs bg-muted px-2 py-1 rounded">{apiKey.prefix}</code>
        </td>

        {/* Scopes */}
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {scopesList.map((scope) => (
              <Badge key={scope} variant="secondary" className="text-xs">
                {scope}
              </Badge>
            ))}
          </div>
        </td>

        {/* Created */}
        <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(apiKey.createdAt)}</td>

        {/* Last Used */}
        <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(apiKey.lastUsedAt)}</td>

        {/* Expires */}
        <td className="px-4 py-3">
          {isEditing ? (
            <Input
              type="datetime-local"
              value={editExpirationDate}
              onChange={(e) => setEditExpirationDate(e.target.value)}
              className="w-[180px]"
            />
          ) : (
            <span className="text-sm">{formatDateTime(apiKey.expirationDate)}</span>
          )}
        </td>

        {/* Actions */}
        <td className="px-4 py-3 text-right">
          {isEditing ? (
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={handleCancel} disabled={isSaving}>
                <X className="h-4 w-4" />
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                disabled={isSaving || !!nameError}
              >
                <Save className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <>
              <Button
                ref={buttonRef}
                variant="ghost"
                size="sm"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="h-8 w-8 p-0"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
              {isMenuOpen &&
                ReactDOM.createPortal(
                  <div
                    ref={menuRef}
                    className="fixed z-[9999] min-w-[120px] bg-popover border border-border rounded-md shadow-md py-1"
                    style={{
                      top: menuPosition.top,
                      left: menuPosition.left,
                    }}
                  >
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-sm text-left hover:bg-muted flex items-center gap-2"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsEditing(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                      Edit
                    </button>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-sm text-left hover:bg-muted flex items-center gap-2 text-red-600"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  </div>,
                  document.body,
                )}
            </>
          )}
        </td>
      </tr>

      <DeleteApiKeyDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        keyName={apiKey.name}
      />
    </>
  );
}

ApiKeyTableRow.propTypes = {
  apiKey: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    prefix: PropTypes.string.isRequired,
    scopes: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    createdAt: PropTypes.string.isRequired,
    lastUsedAt: PropTypes.string.isRequired,
    expirationDate: PropTypes.string.isRequired,
  }).isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onApiKeyUpdated: PropTypes.func.isRequired,
  onApiKeyDeleted: PropTypes.func.isRequired,
};

export default ApiKeyTableRow;
